import isEmpty from 'lodash-es/isEmpty';
import { Resource } from '../Resource';
import { apiVersion } from '../apiVersion';
import { nullToUndefined } from '../core/utils';
import { BillingStatus } from '../enums';
import * as methods from '../methods';
import seeds from './seeds';
const rootPath = 'me';
const phoneNumbersPath = `${rootPath}/phone_numbers`;
/**
 * @zod
 * A kind of mailing, used inside {@link UnsubscribeMailings}
 */
export var MailingKind;
(function (MailingKind) {
    MailingKind[MailingKind["freemiumOnboarding"] = 0] = "freemiumOnboarding";
    MailingKind[MailingKind["newsLetter"] = 1] = "newsLetter";
    MailingKind[MailingKind["saleOffer"] = 2] = "saleOffer";
})(MailingKind || (MailingKind = {}));
export var NotificationPreferenceType;
(function (NotificationPreferenceType) {
    NotificationPreferenceType[NotificationPreferenceType["Measure"] = 0] = "Measure";
    NotificationPreferenceType[NotificationPreferenceType["DSS"] = 1] = "DSS";
    NotificationPreferenceType[NotificationPreferenceType["Device"] = 2] = "Device";
    NotificationPreferenceType[NotificationPreferenceType["OrgAdmin"] = 3] = "OrgAdmin";
})(NotificationPreferenceType || (NotificationPreferenceType = {}));
export var NotificationChannel;
(function (NotificationChannel) {
    NotificationChannel[NotificationChannel["Mail"] = 0] = "Mail";
    NotificationChannel[NotificationChannel["SMS"] = 1] = "SMS";
    NotificationChannel[NotificationChannel["Push"] = 2] = "Push";
})(NotificationChannel || (NotificationChannel = {}));
export const fromRaw = {
    mailingPreferencesUnsubscribe: (x) => x,
    me: ({ date_joined, email, first_name, last_name, is_email_verified, is_staff, is_superuser, last_login, id }) => ({
        id,
        joinedAt: date_joined,
        email,
        firstName: first_name,
        lastName: last_name,
        isEmailVerified: is_email_verified,
        isStaff: is_staff,
        isSuperuser: is_superuser,
        lastSeen: last_login
    }),
    myProfile: ({ language, units, newsletter, cgu_accepted, favorite_org, sso_actura_enabled, sso_actura_user_id, pp_accepted }) => ({
        language,
        isSubscribedToNewsletter: newsletter,
        hasAcceptedTermsOfService: cgu_accepted,
        hasAcceptedPolicyPrivacy: pp_accepted,
        units,
        favoriteOrgId: favorite_org,
        acturaEnabled: nullToUndefined(sso_actura_enabled),
        acturaUserId: nullToUndefined(sso_actura_user_id)
    }),
    myInfo: ({ subscriptions }) => ({
        subscriptions: {
            essential: subscriptions[BillingStatus.essential],
            expert: subscriptions[BillingStatus.expert],
            plus: subscriptions[BillingStatus.plus]
        }
    }),
    myPhoneNumber: (raw) => ({
        countryCode: isEmpty(raw.country_code) ? null : raw.country_code,
        id: raw.id,
        isEnabled: raw.enabled,
        isPrimary: raw.primary,
        number: raw.national_number,
        userId: raw.user
    }),
    myPhoneNumbers: (rawPhones) => rawPhones.map((raw) => ({
        countryCode: isEmpty(raw.country_code) ? null : raw.country_code,
        id: raw.id,
        isEnabled: raw.enabled,
        isPrimary: raw.primary,
        number: raw.national_number,
        userId: raw.user
    })),
    sendFcmToken: ({ token, device_id, name, os }) => ({
        fcmToken: token,
        deviceId: device_id,
        name,
        os
    }),
    notificationPreferences: (raw) => raw
};
const toRaw = {
    mailingPreferencesUnsubscribe: (x) => x,
    myProfile: ({ favoriteOrgId, hasAcceptedTermsOfService, hasAcceptedPolicyPrivacy, isSubscribedToNewsletter, language, units, acturaEnabled, acturaUserId }) => ({
        language,
        units,
        cgu_accepted: hasAcceptedTermsOfService,
        pp_accepted: hasAcceptedPolicyPrivacy,
        favorite_org: favoriteOrgId,
        newsletter: isSubscribedToNewsletter,
        sso_actura_enabled: acturaEnabled,
        sso_actura_user_id: acturaUserId
    }),
    sendFcmToken: ({ fcmToken, deviceId, name, os }) => ({
        token: fcmToken,
        device_id: deviceId,
        name,
        os
    }),
    me: (me) => ({ last_name: me.lastName, first_name: me.firstName }),
    notificationPreferences: (form) => form,
    enablePhoneNumber: ({ isEnabled }) => ({ enabled: isEnabled }),
    myPhoneNumber: (initial) => ({
        country_code: initial.countryCode,
        id: initial.id,
        enabled: initial.isEnabled,
        primary: initial.isPrimary,
        national_number: initial.number,
        user: initial.userId
    }),
    myPhoneNumbers: (initialPhones) => initialPhones.map((initial) => ({
        country_code: initial.countryCode,
        id: initial.id,
        enabled: initial.isEnabled,
        primary: initial.isPrimary,
        national_number: initial.number,
        user: initial.userId
    }))
};
const cacheConfig = {
    staleTime: 20 * 60 * 1000,
    cacheTime: 20 * 60 * 1000
};
const loginAsHeader = (params) => params?.loginAsEmail
    ? {
        headers: {
            'x-weenat-login-as': params?.loginAsEmail
        }
    }
    : {};
const me = Resource((requesterBuilder, config) => {
    const notificationPreferences = {
        getAll: () => requesterBuilder({
            path: `${rootPath}/notifications/preferences`,
            requestCtx: {
                transform: (raw) => raw.map(fromRaw.notificationPreferences)
            },
            config: cacheConfig
        }),
        get: (type) => requesterBuilder({
            path: `${rootPath}/notifications/preferences/${type}`,
            requestCtx: {
                transform: fromRaw.notificationPreferences
            },
            config: cacheConfig
        })
    };
    const get = (params) => requesterBuilder({
        path: rootPath,
        requestCtx: {
            transform: fromRaw.me,
            ...loginAsHeader(params)
        },
        config: cacheConfig
    });
    const getMyProfile = (params) => requesterBuilder({
        path: `${rootPath}/profile`,
        requestCtx: {
            transform: fromRaw.myProfile,
            ...loginAsHeader(params)
        },
        config: cacheConfig
    });
    const getPhoneNumbers = () => requesterBuilder({
        path: phoneNumbersPath,
        requestCtx: {
            transform: fromRaw.myPhoneNumbers
        },
        config: cacheConfig
    });
    return {
        get,
        update: () => requesterBuilder({
            path: rootPath,
            requestCtx: { method: 'PATCH', transform: fromRaw.me, toRaw: toRaw.me },
            config: cacheConfig,
            keysToInvalidate: [get().invalidationKey]
        }),
        getMyProfile,
        updateMyProfile: () => requesterBuilder({
            path: `${rootPath}/profile`,
            requestCtx: {
                method: 'PATCH',
                toRaw: toRaw.myProfile,
                transform: fromRaw.myProfile,
                fromError: (e) => ({
                    fieldErrors: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        //@ts-ignore
                        // After changes on back end phone numbers (January 2023) errors seems to be located on e.fieldErrors.phone
                        telephoneNumber: e.fieldErrors?.phone?.non_field_errors || e.fieldErrors?.phone
                    },
                    nonFieldErrors: e.nonFieldErrors
                })
            },
            keysToInvalidate: [
                getMyProfile().invalidationKey,
                seeds(requesterBuilder, config).getMany().invalidationKey
            ]
        }),
        /** NOT SUPPORTED BY THE BACKEND YET SEE APP-442 */
        deleteMyAccount: () => requesterBuilder({
            path: `${rootPath}/profile`,
            requestCtx: {
                method: 'DELETE',
                parse: async (x) => x
            }
        }),
        getMyInfo: () => requesterBuilder({
            path: `${rootPath}/info`,
            requestCtx: { transform: fromRaw.myInfo, ...apiVersion[1.1] },
            config: cacheConfig
        }),
        sendFcmToken: methods.create({
            rootPath: `${rootPath}/notifications/devices/register`,
            fromRaw: fromRaw.sendFcmToken,
            toRaw: toRaw.sendFcmToken,
            requesterBuilder
        }),
        acceptPrivacyPolicy: () => requesterBuilder({
            path: `${rootPath}/accept_pp`,
            requestCtx: {
                method: 'POST',
                parse: async (x) => x
            }
        }),
        acceptTermsOfService: () => requesterBuilder({
            path: `${rootPath}/accept_cgu`,
            requestCtx: {
                method: 'POST',
                parse: async (x) => x
            }
        }),
        mailingPreferences: {
            unsubscribe: methods.create({
                rootPath: `${rootPath}/unsubscription`,
                fromRaw: fromRaw.mailingPreferencesUnsubscribe,
                toRaw: toRaw.mailingPreferencesUnsubscribe,
                parse: async (x) => x,
                requesterBuilder
            })
        },
        notificationPreferences: {
            getAll: notificationPreferences.getAll,
            get: notificationPreferences.get,
            edit: (type) => requesterBuilder({
                path: `${rootPath}/notifications/preferences/${type}`,
                requestCtx: {
                    method: 'PATCH',
                    transform: fromRaw.notificationPreferences,
                    toRaw: toRaw.notificationPreferences
                },
                config: cacheConfig,
                keysToInvalidate: [
                    notificationPreferences.get(type).invalidationKey,
                    notificationPreferences.getAll().invalidationKey
                ]
            }),
            replace: (type) => requesterBuilder({
                path: `${rootPath}/notifications/preferences/${type}`,
                requestCtx: {
                    method: 'PUT',
                    transform: fromRaw.notificationPreferences,
                    toRaw: toRaw.notificationPreferences
                },
                config: cacheConfig,
                keysToInvalidate: [
                    notificationPreferences.get(type).invalidationKey,
                    notificationPreferences.getAll().invalidationKey
                ]
            })
        },
        phoneNumbers: {
            getAll: getPhoneNumbers,
            updateAll: () => requesterBuilder({
                path: `${phoneNumbersPath}`,
                requestCtx: {
                    method: 'POST',
                    toRaw: toRaw.myPhoneNumbers,
                    parse: async (x) => x
                },
                config: cacheConfig,
                keysToInvalidate: [getPhoneNumbers().invalidationKey]
            }),
            delete: methods.deletion({
                rootPath: phoneNumbersPath,
                requesterBuilder,
                buildKeysToInvalidate: () => [getPhoneNumbers().invalidationKey]
            }),
            enable: methods.update({
                method: 'PATCH',
                requesterBuilder,
                rootPath: phoneNumbersPath,
                fromRaw: fromRaw.myPhoneNumber,
                toRaw: toRaw.enablePhoneNumber,
                buildKeysToInvalidate: () => [getPhoneNumbers().invalidationKey]
            })
        }
    };
});
export default me;
